import { Link, useNavigate } from "react-router-dom";
import { X, Trash2, Plus, Minus } from "lucide-react";
import { useCart } from "../context/CartContext";

export default function CartDrawer() {
  const { items, open, setOpen, update, remove, subtotal, shipping, hasPhysical, count, lineKey } = useCart();
  const navigate = useNavigate();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-[#1c1815]/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <aside className="absolute top-0 right-0 h-full w-full sm:w-[420px] bg-[#faf4e9] border-l border-[#e5dccd] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-[#e5dccd]">
          <div>
            <div className="font-display font-extrabold text-2xl text-[#1c1815]">Your Bag</div>
            <div className="text-xs text-[#8a8076] font-script text-base">{count} item{count === 1 ? "" : "s"}</div>
          </div>
          <button data-testid="cart-close" onClick={() => setOpen(false)} className="p-2 hover:bg-[#efe5d4] rounded-full transition-colors">
            <X size={20} className="text-[#1c1815]"/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-20">
              <div className="font-script text-4xl text-[#e8478e] mb-2">It's Empty In Here</div>
              <p className="text-sm text-[#4a4239] mb-6">Time to fill it with something fabulous.</p>
              <button onClick={() => { setOpen(false); navigate("/shop"); }} data-testid="empty-cart-shop" className="bg-[#2eb6a8] hover:bg-[#1f8d83] text-white font-bold uppercase tracking-[0.18em] text-xs px-6 py-3 rounded-full transition-all hover:shadow-lg">
                Shop Now
              </button>
            </div>
          )}

          {items.map((it) => {
            const k = lineKey(it);
            return (
            <div key={k} className="flex gap-3 bg-white rounded-2xl p-3 border border-[#e5dccd]" data-testid={`cart-item-${k}`}>
              <Link to={`/product/${it.slug}`} onClick={() => setOpen(false)} className="shrink-0">
                <img src={it.image_url} alt={it.title} className="w-20 h-20 object-cover rounded-xl"/>
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${it.slug}`} onClick={() => setOpen(false)} className="font-bold text-sm leading-tight line-clamp-2 text-[#1c1815] hover:text-[#e8478e]">
                  {it.title}
                </Link>
                {(it.variant_size || it.color) && (
                  <div className="mt-0.5 text-[11px] text-[#1f8d83] font-bold uppercase tracking-[0.18em]">
                    {it.variant_size && <span>Size · {it.variant_size}</span>}
                    {it.variant_size && it.color && <span className="mx-1">·</span>}
                    {it.color && <span>{it.color}</span>}
                  </div>
                )}
                <div className="text-xs text-[#8a8076] mt-1">${it.price.toFixed(2)}</div>
                <div className="mt-2 flex items-center gap-2">
                  <button onClick={() => update(k, it.quantity - 1)} className="p-1.5 border border-[#e5dccd] rounded-md hover:border-[#2eb6a8] hover:text-[#2eb6a8]" data-testid={`qty-dec-${k}`}><Minus size={12}/></button>
                  <span className="text-sm w-6 text-center font-bold text-[#1c1815]">{it.quantity}</span>
                  <button onClick={() => update(k, it.quantity + 1)} className="p-1.5 border border-[#e5dccd] rounded-md hover:border-[#2eb6a8] hover:text-[#2eb6a8]" data-testid={`qty-inc-${k}`}><Plus size={12}/></button>
                  <button onClick={() => remove(k)} className="ml-auto text-[#8a8076] hover:text-[#e8478e]" data-testid={`remove-${k}`}><Trash2 size={14}/></button>
                </div>
              </div>
            </div>
            );
          })}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#e5dccd] p-5 space-y-3 bg-white">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.2em] text-[#8a8076] font-bold">Subtotal</span>
              <span data-testid="cart-subtotal" className="font-display font-extrabold text-2xl text-[#1c1815]">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="uppercase tracking-[0.2em] text-[#8a8076] font-bold">Shipping</span>
              <span data-testid="cart-shipping" className={shipping > 0 ? "font-bold text-[#1c1815]" : "text-[#8a8076]"}>
                {shipping > 0 ? `$${shipping.toFixed(2)}` : "Free"}
              </span>
            </div>
            {hasPhysical && shipping > 0 && (
              <div className="text-[11px] text-[#4a4239] bg-[#2eb6a8]/10 border border-[#2eb6a8]/30 rounded-xl px-3 py-2">
                Add <span className="font-bold text-[#1f8d83]">${(50 - subtotal).toFixed(2)}</span> more for FREE shipping ✨
              </div>
            )}
            <button
              data-testid="checkout-button"
              onClick={() => { setOpen(false); navigate("/checkout"); }}
              className="w-full bg-[#e8478e] hover:bg-[#c93875] text-white font-bold uppercase tracking-[0.18em] py-4 rounded-full transition-all btn-shimmer hover:shadow-lg text-sm"
            >
              Checkout
            </button>
            <button onClick={() => setOpen(false)} className="w-full text-[10px] uppercase tracking-[0.25em] text-[#8a8076] hover:text-[#1c1815] py-2 font-bold">
              Keep Shopping
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
